import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { findAgencyByName } from './agencyTransferUtils';

const PHASE = {
  CLOSED: 'closed',
  READY: 'ready',
  SUBMITTING: 'submitting',
  VERIFYING: 'verifying',
  VERIFICATION_FAILED: 'verification-failed',
  REFRESHING: 'refreshing',
  COMPLETED: 'completed',
};

const initialState = {
  phase: PHASE.CLOSED,
  pilgrim: null,
  flow: null,
};

const unwrapAPIResponse = (response) => response?.data ?? response;

const mergeTripSnapshot = (previous, incoming) => {
  if (!incoming || typeof incoming !== 'object') return previous;
  const merged = { ...(previous || {}), ...incoming };
  if (incoming.deposits == null && previous?.deposits !== undefined) merged.deposits = previous.deposits;
  return merged;
};

const reducer = (state, event) => {
  switch (event.type) {
    case 'OPEN':
      return { phase: PHASE.READY, pilgrim: event.pilgrim, flow: event.flow };
    case 'SUBMIT':
      return { ...state, phase: PHASE.SUBMITTING, flow: event.flow };
    case 'SUBMIT_REJECTED':
      return { ...state, phase: PHASE.READY };
    case 'VERIFY':
      return { ...state, phase: PHASE.VERIFYING };
    case 'VERIFICATION_FAILED':
      return { ...state, phase: PHASE.VERIFICATION_FAILED };
    case 'TRANSFER_NOT_APPLIED':
      return {
        phase: PHASE.READY,
        pilgrim: event.pilgrim,
        flow: { ...state.flow, targetAgencyId: null, tripSnapshot: event.pilgrim },
      };
    case 'TRANSFER_COMPLETED':
      return {
        phase: PHASE.REFRESHING,
        pilgrim: event.pilgrim,
        flow: { ...state.flow, tripSnapshot: event.pilgrim },
      };
    case 'REFRESH':
      return { ...state, phase: PHASE.REFRESHING };
    case 'REFRESHED':
      return {
        ...state,
        phase: PHASE.COMPLETED,
        pilgrim: event.pilgrim || state.pilgrim,
        flow: event.pilgrim ? { ...state.flow, tripSnapshot: event.pilgrim } : state.flow,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const isPendingPhase = (phase) => phase === PHASE.SUBMITTING || phase === PHASE.VERIFYING || phase === PHASE.REFRESHING;

const isModalPhase = (phase) => phase !== PHASE.CLOSED && phase !== PHASE.REFRESHING && phase !== PHASE.COMPLETED;

const useAgencyTransfer = ({ api, queryClient, agencies, isAuthorized, scopeKey, onTripChange }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const activityRef = useRef({ sequence: 0, scopeKey });
  activityRef.current.scopeKey = scopeKey;

  const isActive = ({ session, scope }) =>
    activityRef.current.sequence === session && activityRef.current.scopeKey === scope;

  const invalidateTransferQueries = () =>
    Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: ['trip/'] }),
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] }),
      queryClient.invalidateQueries({ queryKey: ['agency/'] }),
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] }),
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] }),
      queryClient.invalidateQueries({ queryKey: ['trip/stats/trip_category_paid/'] }),
      queryClient.invalidateQueries({ queryKey: ['trip/trip_report/'] }),
    ]);

  const refreshTransferredTrip = async (flow) => {
    if (!isActive(flow)) return;
    dispatch({ type: 'REFRESH' });
    const [detailResult] = await Promise.allSettled([api.get(`trip/${flow.tripId}/`), invalidateTransferQueries()]);
    if (!isActive(flow)) return;

    if (detailResult.status === 'fulfilled') {
      const refreshedTrip = mergeTripSnapshot(flow.tripSnapshot, unwrapAPIResponse(detailResult.value));
      onTripChange(flow.tripId, refreshedTrip);
      dispatch({ type: 'REFRESHED', pilgrim: refreshedTrip });
      return;
    }

    dispatch({ type: 'REFRESHED' });
    toast.error('Le transfert est effectué, mais les données actualisées ne sont pas disponibles.', {
      action: {
        label: 'Réessayer',
        onClick: () => refreshTransferredTrip(flow),
      },
    });
  };

  const completeTransfer = (response, flow) => {
    if (!isActive(flow)) return;
    const transferredTrip = mergeTripSnapshot(flow.tripSnapshot, unwrapAPIResponse(response));
    const completedFlow = { ...flow, tripSnapshot: transferredTrip };
    onTripChange(flow.tripId, transferredTrip);
    dispatch({ type: 'TRANSFER_COMPLETED', pilgrim: transferredTrip });
    toast.success('Transfert effectué avec succès.');
    refreshTransferredTrip(completedFlow);
  };

  const reconcileUncertainTransfer = async (flow) => {
    if (!flow || !isActive(flow)) return;
    dispatch({ type: 'VERIFY' });
    try {
      const response = await api.get(`trip/${flow.tripId}/`);
      if (!isActive(flow)) return;
      const refreshedTrip = mergeTripSnapshot(flow.tripSnapshot, unwrapAPIResponse(response));
      if (findAgencyByName(refreshedTrip?.agency_name, agencies)?.id === flow.targetAgencyId) {
        completeTransfer(response, flow);
        return;
      }
      onTripChange(flow.tripId, refreshedTrip);
      dispatch({ type: 'TRANSFER_NOT_APPLIED', pilgrim: refreshedTrip });
      toast.error('Oups ! Une erreur est survenue.');
    } catch {
      if (isActive(flow)) dispatch({ type: 'VERIFICATION_FAILED' });
    }
  };

  const { mutate: transferAgency } = useMutation({
    retry: false,
    meta: { agloCampaignWrite: true },
    mutationFn: ({ tripId, targetAgencyId }) => api.post(`trip/${tripId}/transfer_agency/${targetAgencyId}/`),
    onSuccess: (response, flow) => completeTransfer(response, flow),
    onError: (error, flow) => {
      if (!isActive(flow)) return;
      if (typeof error === 'string') {
        dispatch({ type: 'SUBMIT_REJECTED' });
        toast.error(error);
        return;
      }
      reconcileUncertainTransfer(flow);
    },
  });

  const reset = useCallback(() => {
    activityRef.current.sequence += 1;
    dispatch({ type: 'RESET' });
  }, []);

  useEffect(() => {
    activityRef.current.sequence += 1;
    dispatch({ type: 'RESET' });
  }, [scopeKey]);

  const open = (trip) => {
    if (!isAuthorized || !trip?.id || isPendingPhase(state.phase)) return false;
    const flow = {
      session: activityRef.current.sequence + 1,
      scope: scopeKey,
      tripId: trip.id,
      targetAgencyId: null,
      tripSnapshot: trip,
    };
    activityRef.current.sequence = flow.session;
    dispatch({ type: 'OPEN', pilgrim: trip, flow });
    return true;
  };

  const close = () => {
    if (isPendingPhase(state.phase)) return false;
    reset();
    return true;
  };

  const submit = (targetAgencyId) => {
    const targetExists = agencies?.some((agency) => agency.id === targetAgencyId);
    const sourceAgencyId = findAgencyByName(state.pilgrim?.agency_name, agencies)?.id;
    if (
      state.phase !== PHASE.READY ||
      !isAuthorized ||
      !state.pilgrim?.id ||
      !sourceAgencyId ||
      sourceAgencyId === targetAgencyId ||
      !targetExists
    )
      return;

    const flow = {
      ...state.flow,
      targetAgencyId,
      tripSnapshot: state.pilgrim,
    };
    dispatch({ type: 'SUBMIT', flow });
    transferAgency(flow);
  };

  const retryVerification = () => {
    if (state.phase === PHASE.VERIFICATION_FAILED) reconcileUncertainTransfer(state.flow);
  };

  return {
    session: state.flow?.session,
    pilgrim: state.pilgrim,
    isOpen: isModalPhase(state.phase),
    isPending: isPendingPhase(state.phase),
    isSubmitting: state.phase === PHASE.SUBMITTING,
    isReconciling: state.phase === PHASE.VERIFYING,
    isRetryBlocked: state.phase === PHASE.VERIFYING || state.phase === PHASE.VERIFICATION_FAILED,
    transferError:
      state.phase === PHASE.VERIFYING
        ? 'Le résultat du transfert est incertain. Vérification en cours…'
        : state.phase === PHASE.VERIFICATION_FAILED
        ? 'Impossible de vérifier le résultat du transfert. Réessayez la vérification.'
        : null,
    open,
    close,
    reset,
    retryVerification,
    submit,
  };
};

export default useAgencyTransfer;
